import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Divider,
  Chip,
  Avatar,
  IconButton,
  Stack
} from '@mui/material';
import { 
  Person as PersonIcon,
  Gavel as GavelIcon,
  Train as TrainIcon,
  Notifications as NotificationsIcon,
  FiberManualRecord as FiberManualRecordIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  Groups as GroupsIcon,
  School as SchoolIcon,
  Handshake as HandshakeIcon,
  Campaign as CampaignIcon,
  Support as SupportIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Image as ImageIcon,
  CalendarToday,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { 
  collection, 
  getDocs, 
  query, 
  orderBy, 
  limit,
  onSnapshot,
  where,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../../firebase';
import { format } from 'date-fns';
import CircularProgress from '@mui/material/CircularProgress';
import ManagingCommitteeDisplay from '../../components/ManagingCommitteeDisplay';

function Home() {
  const navigate = useNavigate();
  const [courtRooms, setCourtRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notices, setNotices] = useState([]);
  const [openNoticesModal, setOpenNoticesModal] = useState(false);
  const [greeting, setGreeting] = useState(null);
  const [showGreeting, setShowGreeting] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [latestNews, setLatestNews] = useState([]);
  const [committeeMembers, setCommitteeMembers] = useState([]);
  const [loadingCommittee, setLoadingCommittee] = useState(true);
  const [selectedNews, setSelectedNews] = useState(null);
  const [openNewsDialog, setOpenNewsDialog] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);

  // Preload image function
  const preloadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = url;
    });
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'courtRooms'), (snapshot) => {
      const rooms = [];
      snapshot.forEach((doc) => {
        rooms.push({ ...doc.data(), id: doc.id });
      });
      setCourtRooms(rooms.sort((a, b) => {
        if (a.number === 'Railway Mobile') return 1;
        if (b.number === 'Railway Mobile') return -1;
        return parseInt(a.number) - parseInt(b.number);
      }));
      setLoading(false);
    });

    // Fetch active notices
    const noticesUnsubscribe = onSnapshot(
      query(collection(db, 'notices'), where('active', '==', true)),
      (snapshot) => {
        const noticesList = [];
        snapshot.forEach((doc) => {
          noticesList.push(doc.data());
        });
        setNotices(noticesList);
      }
    );

    // Fetch active greeting
    const greetingUnsubscribe = onSnapshot(
      query(collection(db, 'greetings'), where('active', '==', true)),
      (snapshot) => {
        if (!snapshot.empty) {
          const greetingData = snapshot.docs[0].data();
          setGreeting(greetingData);
          
          // Show greeting after 3 seconds
          const showTimer = setTimeout(() => {
            setShowGreeting(true);
            
            // Hide after 10 seconds
            const hideTimer = setTimeout(() => {
              setShowGreeting(false);
            }, 10000);

            return () => clearTimeout(hideTimer);
          }, 3000);

          return () => clearTimeout(showTimer);
        } else {
          setGreeting(null);
          setShowGreeting(false);
        }
      }
    );

    const fetchLatestNews = async () => {
      try {
        const newsCollection = collection(db, 'news');
        const newsQuery = query(
          newsCollection,
          orderBy('timestamp', 'desc'),
          limit(3)
        );
        const querySnapshot = await getDocs(newsQuery);
        const newsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          paragraphs: doc.data().content.split('\n\n'),
          timestamp: doc.data().timestamp?.toDate() || new Date() // Convert Firestore timestamp to JS Date
        }));
        setLatestNews(newsData);
      } catch (error) {
        console.error('Error fetching news:', error);
      }
    };

    fetchLatestNews();

    const getCurrentFinancialYear = () => {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth() + 1;
      return month < 4 ? `${year-1}-${year}` : `${year}-${year+1}`;
    };

    const fetchCommitteeMembers = async () => {
      try {
        const currentYear = getCurrentFinancialYear();
        const docRef = doc(db, 'managingCommittee', currentYear);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const yearData = docSnap.data();
          const membersArray = yearData.members || [];
          // Sort members by position
          const positions = ['PRESIDENT', 'VICE-PRESIDENT', 'SECRETARY', 'JOINT-SECRETARY', 'TREASURER'];
          membersArray.sort((a, b) => {
            const posA = positions.indexOf(a.position);
            const posB = positions.indexOf(b.position);
            return posA - posB;
          });
          setCommitteeMembers(membersArray);
        }
      } catch (error) {
        console.error('Error fetching committee members:', error);
      } finally {
        setLoadingCommittee(false);
      }
    };

    fetchCommitteeMembers();

    return () => {
      unsubscribe();
      noticesUnsubscribe();
      greetingUnsubscribe();
    };
  }, []);

  const handleMembershipClick = () => {
    navigate('/membership-application');
  };

  const getPositionDisplay = (position) => {
    const displayMap = {
      'PRESIDENT': 'President',
      'VICE-PRESIDENT': 'Vice President',
      'SECRETARY': 'Secretary',
      'JOINT-SECRETARY': 'Joint Secretary',
      'TREASURER': 'Treasurer'
    };
    return displayMap[position] || position;
  };

  const president = committeeMembers.find(m => m.position === 'PRESIDENT');
  const otherMembers = committeeMembers.filter(m => 
    m.position !== 'PRESIDENT' && 
    m.position !== 'CO-OPTED MEMBER'
  );

  const handleOpenNews = (newsItem) => {
    setSelectedNews(newsItem);
    setOpenNewsDialog(true);
  };

  const handleCloseNews = () => {
    setSelectedNews(null);
    setOpenNewsDialog(false);
  };

  const handleAttachmentClick = (attachment, event) => {
    event.stopPropagation();
    if (attachment.type === 'application/pdf') {
      window.open(attachment.url, '_blank');
    } else if (attachment.type.startsWith('image/')) {
      setSelectedAttachment(attachment);
    }
  };

  const handleCloseAttachment = () => {
    setSelectedAttachment(null);
  };

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh' }}>
      {/* Greeting Card */}
      {showGreeting && greeting && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            width: '200px',
            bgcolor: '#FFF9E6',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
            zIndex: 1000,
            animation: 'slideIn 0.5s ease-out forwards',
            '@keyframes slideIn': {
              '0%': { transform: 'translateX(100%)' },
              '100%': { transform: 'translateX(0)' }
            }
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={() => setShowGreeting(false)}
            size="small"
            sx={{
              position: 'absolute',
              right: 4,
              top: 4,
              bgcolor: 'rgba(0,0,0,0.1)',
              padding: '4px',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.2)'
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box sx={{ p: 1 }}>
            <img
              src={greeting?.imageUrl}
              alt="Greeting"
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: '8px',
                display: 'block'
              }}
            />
          </Box>
        </Box>
      )}

      {/* Notice Bar */}
      {notices.length > 0 && (
        <>
          <Box
            onClick={() => setOpenNoticesModal(true)}
            sx={{
              width: '100%',
              bgcolor: '#580100',
              color: 'white',
              py: 1,
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: '#6a0100',
              }
            }}
          >
            {/* Fixed Notice: label */}
            <Box
              sx={{
                px: 3,
                borderRight: '2px solid rgba(255,255,255,0.3)',
                display: 'flex',
                alignItems: 'center',
                zIndex: 1,
                bgcolor: 'inherit'
              }}
            >
              <Typography
                variant="body2"
                component="span"
                sx={{ 
                  fontWeight: 700,
                  whiteSpace: 'nowrap'
                }}
              >
                NOTICE:
              </Typography>
            </Box>

            {/* Scrolling notices */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <Box
                sx={{
                  display: 'flex',
                  whiteSpace: 'nowrap',
                  animation: 'marquee 30s linear infinite',
                  '@keyframes marquee': {
                    '0%': { transform: 'translateX(100%)' },
                    '100%': { transform: 'translateX(-100%)' }
                  }
                }}
              >
                {[...notices, ...notices].map((notice, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    component="span"
                    sx={{
                      fontWeight: 500,
                      mx: 4,
                      '&::before': {
                        content: '"•"',
                        display: 'inline-block',
                        marginRight: '8px'
                      }
                    }}
                  >
                    {notice.text}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Notices Modal */}
          <Dialog
            open={openNoticesModal}
            onClose={() => setOpenNoticesModal(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                bgcolor: '#FFF9E6', // Light yellow background
                backgroundImage: 'linear-gradient(rgba(255,255,255,0.8), rgba(255,255,255,0.8))',
                borderRadius: '8px',
                overflow: 'hidden'
              }
            }}
          >
            <DialogTitle
              sx={{
                bgcolor: '#580100',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 2
              }}
            >
              <NotificationsIcon />
              Important Notices
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <List sx={{ p: 0 }}>
                {notices.map((notice, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      borderBottom: index < notices.length - 1 ? '1px solid rgba(88,1,0,0.1)' : 'none',
                      py: 2,
                      px: 3,
                      '&:hover': {
                        bgcolor: 'rgba(88,1,0,0.05)'
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <FiberManualRecordIcon sx={{ color: '#580100', fontSize: 12 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={notice.text}
                      primaryTypographyProps={{
                        sx: {
                          color: '#580100',
                          fontWeight: 500,
                          fontSize: '1rem'
                        }
                      }}
                      secondary={notice.timestamp ? new Date(notice.timestamp.toDate()).toLocaleString('en-US', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : ''}
                      secondaryTypographyProps={{
                        sx: {
                          color: 'rgba(88,1,0,0.7)',
                          fontSize: '0.875rem',
                          mt: 0.5
                        }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </DialogContent>
            <DialogActions sx={{ p: 2, bgcolor: '#FFF9E6' }}>
              <Button
                onClick={() => setOpenNoticesModal(false)}
                variant="contained"
                sx={{
                  bgcolor: '#580100',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#6a0100'
                  }
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          pt: { xs: 10, sm: 8, md: 4 },
          pb: { xs: 8, md: 12 },
          overflow: 'hidden',
          backgroundImage: 'url(/library.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.85)',
            zIndex: 0
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at center, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0) 60%)',
            pointerEvents: 'none',
            zIndex: 1
          }
        }}
      >
        <Container 
          maxWidth="lg" 
          sx={{ 
            position: 'relative',
            zIndex: 2 
          }}
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={12}>
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                   textAlign: 'center',
                }}
              ><br  /><br  /> <br  /><br  />
                <Typography
                  variant="h2"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontFamily: 'Algerian',
                    fontWeight: 900,
                    color: '#1e1e1e',
                    mb: 3,
                   
                    textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
                    fontSize: { 
                      xs: '2.2rem',
                      sm: '3rem',
                      md: '3.8rem',
                      lg: '4.2rem'
                    },
                    lineHeight: {
                      xs: 1.1,
                      md: 1
                    },
                    letterSpacing: '0.02em',
                    color: '#580200',
                  }}
                >
                  ANDHERI COURT'S BAR ASSOCIATION
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 4,
                    color: 'rgba(0,0,0,0.7)',
                    fontWeight: 400,
                    lineHeight: 1.6,
                    textAlign: 'center',
                    width: '100%'
                  }}
                >
                  Serving justice and fostering legal excellence
                  <br />
                  in the heart of Mumbai
                </Typography>
                {/* <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    component={Link}
                    to="/membership-application"
                    sx={{
                      bgcolor: '#580100',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
                      '&:hover': {
                        bgcolor: '#3d0100',
                        boxShadow: '0 6px 8px rgba(0,0,0,0.3)'
                      }
                    }}
                  >
                    Become a Member
                  </Button>
                  <Button
                    variant="outlined"
                    component={Link}
                    to="/about"
                    sx={{
                      color: '#333',
                      borderColor: '#333',
                      '&:hover': {
                        borderColor: '#333',
                        bgcolor: 'rgba(0,0,0,0.1)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }
                    }}
                  >
                    Learn More
                  </Button>
                </Stack> */}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{
                    mt: 4,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Button
                    component={Link}
                    to="/membership-application"
                    variant="contained"
                    size="large"
                    sx={{
                      bgcolor: '#580100',
                      color: 'white',
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      '&:hover': {
                        bgcolor: '#6e0200'
                      }
                    }}
                  >
                    Become a Member
                  </Button>
                  <Button
                    component={Link}
                    to="/about"
                    variant="outlined"
                    size="large"
                    sx={{
                      borderColor: '#580100',
                      color: '#580100',
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      '&:hover': {
                        borderColor: '#6e0200',
                        color: '#6e0200',
                        bgcolor: 'rgba(88, 1, 0, 0.04)'
                      }
                    }}
                  >
                    Learn More
                  </Button>
                </Stack>
              </Box>
            </Grid>
            {/* <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  height: { xs: '300px', md: '500px' },
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'visible',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '80%',
                    height: '20px',
                    background: 'radial-gradient(circle at center, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 60%)',
                    borderRadius: '50%',
                    filter: 'blur(5px)'
                  }
                }}
              >
                <Box
                  component="img"
                  src="/law-lady.png"
                  alt="Lady Justice"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    position: 'relative',
                    zIndex: 2,
                    filter: 'drop-shadow(0 10px 15px rgba(0,0,0,0.3))',
                    transform: 'scale(1.5)',
                    transformOrigin: 'center center'
                  }}
                />
              </Box>
            </Grid> */}
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h3" component="h1" gutterBottom sx={{ 
              color: '#580100',
              fontWeight: 600,
              mb: 3
            }}>
              The Andheri Court's Bar Association: A Rich History of Legal Excellence
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', textAlign: 'justify' }}>
              The Andheri Court's Bar Association has been the cornerstone of legal excellence and professional growth. With a legacy of fostering justice and supporting legal practitioners, we strive to uphold the highest ethical standards and provide a trusted platform for advancing legal expertise.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="/images/hero-bg.png"
              alt="Andheri Court's Bar Association"
              sx={{
                width: '100%',
                height: 'auto',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Court Rooms Status Section */}
      <Box sx={{ 
        py: 4,
        bgcolor: '#f8f8f8',
        borderTop: '1px solid #eaeaea',
        borderBottom: '1px solid #eaeaea'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
            <Typography 
              variant="h4" 
              component="h2" 
              sx={{ 
                color: '#580100',
                textAlign: 'center',
                fontWeight: 600,
                mr: 2
              }}
            >
              Court Room Status
            </Typography>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: '#666',
                display: 'flex',
                alignItems: 'center' 
              }}
            >
              {new Date().toLocaleDateString('en-IN', { 
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {courtRooms.map((room) => {
              const lastUpdated = room.lastUpdated ? new Date(room.lastUpdated) : null;
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const isOutdated = !lastUpdated || lastUpdated < today;

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={room.id}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      borderTop: '4px solid #580100',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {room.number === 'Railway Mobile' ? (
                        <TrainIcon sx={{ fontSize: 24, color: '#580100', mr: 1 }} />
                      ) : (
                        <GavelIcon sx={{ fontSize: 24, color: '#580100', mr: 1 }} />
                      )}
                      <Typography variant="h6" component="h3" sx={{ color: '#580100' }}>
                        {room.number === 'Railway Mobile' ? 'Railway Mobile' : `Court ${room.number}`}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <PersonIcon sx={{ fontSize: 20, color: '#666', mr: 1 }} />
                        <Typography variant="body1" sx={{ color: '#333' }}>
                          {room.judgeName || 'No judge assigned'}
                        </Typography>
                      </Box>

                      <Chip
                        label={isOutdated ? 'Pending' : (room.isPresent ? 'Presiding' : 'Not Presiding')}
                        color={isOutdated ? 'default' : (room.isPresent ? 'success' : 'error')}
                        size="small"
                        sx={{
                          position: 'absolute',
                          bottom: 16,
                          right: 16,
                          ...(isOutdated && {
                            bgcolor: 'white',
                            border: '1px solid #e0e0e0'
                          })
                        }}
                      />

                      {lastUpdated && !isOutdated && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            position: 'absolute',
                            bottom: 16,
                            left: 16,
                            color: '#666'
                          }}
                        >
                          Updated: {lastUpdated.toLocaleTimeString('en-IN', { 
                            hour: 'numeric',
                            minute: 'numeric',
                            hour12: true
                          })}
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* Updates Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'white' }}>
        <Container 
          maxWidth="lg"
          sx={{ px: { xs: 2, sm: 3, md: 4 } }}
        >
          <Box sx={{ maxWidth: 800, mb: 6 }}>
            <Typography variant="subtitle1" sx={{ color: '#580100', mb: 1, fontWeight: 600 }}>
              Latest Updates
            </Typography>
            <Typography variant="h2" sx={{ color: '#333333', mb: 4, fontWeight: 700 }}>
              Stay Updated with Andheri Court's Bar Association News
            </Typography>
          </Box>
          {latestNews.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No news available at the moment
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Check back later for updates
              </Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={4}>
                {latestNews.map((news) => (
                  <Grid item xs={12} md={4} key={news.id}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                        }
                      }}
                      onClick={() => handleOpenNews(news)}
                    >
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Typography 
                          variant="h6" 
                          gutterBottom 
                          sx={{ 
                            color: '#580100',
                            minHeight: '3em',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {news.title}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          gutterBottom
                          sx={{ mb: 2 }}
                        >
                          {format(news.timestamp, 'MMMM dd, yyyy')}
                        </Typography>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            mb: 2,
                            flexGrow: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            minHeight: '4.5em'
                          }}
                        >
                          {news.paragraphs[0]}
                        </Typography>
                        
                        {news.attachment && (
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              bgcolor: 'rgba(88,1,0,0.05)',
                              p: 1,
                              borderRadius: 1,
                              width: 'fit-content',
                              mt: 'auto'
                            }}
                            onClick={(e) => handleAttachmentClick(news.attachment, e)}
                          >
                            {news.attachment.type === 'application/pdf' ? (
                              <PictureAsPdfIcon sx={{ color: '#580100', mr: 1 }} />
                            ) : (
                              <ImageIcon sx={{ color: '#580100', mr: 1 }} />
                            )}
                            <Typography 
                              variant="body2"
                              sx={{ 
                                color: '#580100',
                                '&:hover': { textDecoration: 'underline' },
                                maxWidth: '200px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {news.attachment.name}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Button
                  component={Link}
                  to="/news"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    color: '#580100',
                    borderColor: '#580100',
                    '&:hover': {
                      borderColor: '#580100',
                      bgcolor: 'rgba(88,1,0,0.1)'
                    }
                  }}
                  variant="outlined"
                >
                  View All News
                </Button>
              </Box>
            </>
          )}
        </Container>
      </Box>

      {/* News Dialog */}
      <Dialog
        open={openNewsDialog}
        onClose={handleCloseNews}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            bgcolor: '#fff',
            backgroundImage: 'linear-gradient(rgba(88,1,0,0.02) 0%, rgba(255,255,255,1) 200px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            '& .MuiDialogTitle-root': {
              py: 3,
              px: 4,
            },
            '& .MuiDialogContent-root': {
              py: 3,
              px: 4,
            },
            '& .MuiDialogActions-root': {
              px: 4,
              py: 2,
              borderTop: '1px solid rgba(0,0,0,0.08)'
            }
          }
        }}
      >
        {selectedNews && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography 
                  variant="h4" 
                  component="h2" 
                  sx={{ 
                    color: '#580100',
                    fontWeight: 600,
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    lineHeight: 1.3
                  }}
                >
                  {selectedNews.title}
                </Typography>
                <IconButton 
                  onClick={handleCloseNews}
                  sx={{ 
                    mt: -1, 
                    mr: -1,
                    color: 'rgba(0,0,0,0.54)',
                    '&:hover': {
                      bgcolor: 'rgba(88,1,0,0.08)'
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <CalendarToday sx={{ fontSize: '1rem', color: 'text.secondary', mr: 1 }} />
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    color: 'text.secondary',
                    fontWeight: 500
                  }}
                >
                  {format(selectedNews.timestamp, 'MMMM dd, yyyy')}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ 
                mb: selectedNews.attachment ? 4 : 0,
                '& p': {
                  fontSize: '1.1rem',
                  lineHeight: 1.7,
                  color: 'text.primary',
                  mb: 2.5,
                  '&:last-child': {
                    mb: 0
                  }
                }
              }}>
                {selectedNews.paragraphs.map((paragraph, index) => (
                  <Typography key={index} paragraph>
                    {paragraph}
                  </Typography>
                ))}
              </Box>
              {selectedNews.attachment && (
                <Paper
                  elevation={0}
                  sx={{ 
                    p: 2,
                    bgcolor: 'rgba(88,1,0,0.03)',
                    border: '1px solid rgba(88,1,0,0.1)',
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(88,1,0,0.06)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                    }
                  }}
                  onClick={(e) => handleAttachmentClick(selectedNews.attachment, e)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {selectedNews.attachment.type === 'application/pdf' ? (
                      <PictureAsPdfIcon sx={{ color: '#580100', fontSize: '2rem', mr: 2 }} />
                    ) : (
                      <ImageIcon sx={{ color: '#580100', fontSize: '2rem', mr: 2 }} />
                    )}
                    <Box>
                      <Typography 
                        variant="subtitle1"
                        sx={{ 
                          color: '#580100',
                          fontWeight: 600,
                          mb: 0.5
                        }}
                      >
                        {selectedNews.attachment.name}
                      </Typography>
                      <Typography 
                        variant="body2"
                        sx={{ 
                          color: 'text.secondary'
                        }}
                      >
                        Click to {selectedNews.attachment.type === 'application/pdf' ? 'open PDF' : 'view image'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={Boolean(selectedAttachment)}
        onClose={handleCloseAttachment}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            bgcolor: '#000',
            backgroundImage: 'none',
            maxHeight: '90vh',
            '& .MuiDialogTitle-root': {
              bgcolor: 'rgba(0,0,0,0.85)',
              backdropFilter: 'blur(10px)',
              color: '#fff',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1
            },
            '& .MuiDialogContent-root': {
              p: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            },
            '& .MuiDialogActions-root': {
              bgcolor: 'rgba(0,0,0,0.85)',
              backdropFilter: 'blur(10px)',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              px: 2
            }
          }
        }}
      >
        {selectedAttachment && selectedAttachment.type.startsWith('image/') && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  {selectedAttachment.name}
                </Typography>
                <IconButton 
                  onClick={handleCloseAttachment}
                  sx={{ 
                    color: '#fff',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box 
                sx={{ 
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 4
                }}
              >
                <img
                  src={selectedAttachment.url}
                  alt={selectedAttachment.name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: 'calc(90vh - 130px)',
                    objectFit: 'contain',
                    borderRadius: 8
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={handleCloseAttachment}
                sx={{ color: '#fff' }}
              >
                Close
              </Button>
              <Button 
                onClick={() => window.open(selectedAttachment.url, '_blank')}
                variant="contained"
                sx={{
                  bgcolor: '#580100',
                  '&:hover': {
                    bgcolor: '#6a0200'
                  }
                }}
              >
                Open in New Tab
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Join Our Legal Community Section */}
      <Box
        sx={{
          bgcolor: '#580100',
          color: 'white',
          py: { xs: 6, md: 8 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: 'rgba(255, 255, 255, 0.95)',
                  fontSize: { xs: '2rem', md: '2.5rem' }
                }}
              >
                Join Our Legal Community
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontWeight: 400,
                  lineHeight: 1.6,
                  textAlign: 'justify'
                }}
              >
                Become a part of Andheri's most prestigious legal association and contribute to the advancement of justice.
              </Typography>
            </Grid>
            <Grid 
              item 
              xs={12} 
              md={4} 
              sx={{ 
                display: 'flex', 
                justifyContent: { xs: 'flex-start', md: 'flex-end' } 
              }}
            >
              <Button
                onClick={handleMembershipClick}
                variant="contained"
                size="large"
                sx={{
                  bgcolor: 'white',
                  color: '#580100',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                  },
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  textTransform: 'none',
                  zIndex: 1
                }}
              >
                Become a Member
              </Button>
            </Grid>
          </Grid>
        </Container>

        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            background: 'linear-gradient(45deg, transparent 45%, rgba(255,255,255,0.1) 45%, rgba(255,255,255,0.1) 55%, transparent 55%)',
            backgroundSize: '20px 20px'
          }}
        />
      </Box>

      {/* Managing Committee Section */}
      {/* <Box sx={{ mb: 8, bgcolor: 'background.paper', py: 4 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, color: '#580100' }}>
            Managing Committee
          </Typography>
          {loadingCommittee ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <ManagingCommitteeDisplay members={committeeMembers} showContactInfo={true} showCoOptedMembers={false} />
          )}
        </Container>
      </Box> */}

      {/* Legal Aid Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          {/* Section Header */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                mb: 2
              }}
            >
              Legal Aid
            </Typography>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 3
              }}
            >
              Commitment to Legal Aid and Justice
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                maxWidth: '800px',
                lineHeight: 1.7,
                textAlign: 'justify'
              }}
            >
              We believe in fostering equal access to justice. Our legal aid services aim to assist those in need.
            </Typography>
          </Box>

          {/* Legal Aid Cards */}
          <Grid container spacing={4}>
            {/* Free Legal Clinics */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: '#580100',
                    bgcolor: 'rgba(88, 1, 0, 0.02)'
                  }
                }}
              >
                <CardContent sx={{ p: 3, flex: 1 }}>
                  <Box 
                    sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: '12px', 
                      bgcolor: 'rgba(88, 1, 0, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <GavelIcon sx={{ color: '#580100', fontSize: 24 }} />
                  </Box>
                  <Typography 
                    variant="h6" 
                    component="h3" 
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Free Legal Clinics
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, textAlign: 'justify' }}>
                    Regularly held for individuals unable to afford legal representation.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Pro Bono Advocacy */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: '#580100',
                    bgcolor: 'rgba(88, 1, 0, 0.02)'
                  }
                }}
              >
                <CardContent sx={{ p: 3, flex: 1 }}>
                  <Box 
                    sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: '12px', 
                      bgcolor: 'rgba(88, 1, 0, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <HandshakeIcon sx={{ color: '#580100', fontSize: 24 }} />
                  </Box>
                  <Typography 
                    variant="h6" 
                    component="h3" 
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Pro Bono Advocacy
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, textAlign: 'justify' }}>
                    Volunteer advocates offering representation for critical cases.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Community Outreach */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: '#580100',
                    bgcolor: 'rgba(88, 1, 0, 0.02)'
                  }
                }}
              >
                <CardContent sx={{ p: 3, flex: 1 }}>
                  <Box 
                    sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: '12px', 
                      bgcolor: 'rgba(88, 1, 0, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <CampaignIcon sx={{ color: '#580100', fontSize: 24 }} />
                  </Box>
                  <Typography 
                    variant="h6" 
                    component="h3" 
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Community Outreach
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, textAlign: 'justify' }}>
                    Legal awareness camps in local communities.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Support for Underprivileged */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: '#580100',
                    bgcolor: 'rgba(88, 1, 0, 0.02)'
                  }
                }}
              >
                <CardContent sx={{ p: 3, flex: 1 }}>
                  <Box 
                    sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: '12px', 
                      bgcolor: 'rgba(88, 1, 0, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <SupportIcon sx={{ color: '#580100', fontSize: 24 }} />
                  </Box>
                  <Typography 
                    variant="h6" 
                    component="h3" 
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Support for Underprivileged
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, textAlign: 'justify' }}>
                    Guidance and assistance for individuals navigating complex legal issues.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          {/* Section Header */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                mb: 2
              }}
            >
              Bar Association Services
            </Typography>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 3
              }}
            >
              Services for Our Members and the Community
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                maxWidth: '800px',
                lineHeight: 1.7,
                textAlign: 'justify'
              }}
            >
              Explore the range of services we offer to support advocates and enhance access to justice.
            </Typography>
          </Box>

          {/* Services Cards */}
          <Grid container spacing={4}>
            {/* Legal Documentation Support */}
            <Grid item xs={12} md={4}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: '#580100',
                    bgcolor: 'rgba(88, 1, 0, 0.02)'
                  }
                }}
              >
                <CardContent sx={{ p: 3, flex: 1 }}>
                  <Box 
                    sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: '12px', 
                      bgcolor: 'rgba(88, 1, 0, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <DescriptionIcon sx={{ color: '#580100', fontSize: 24 }} />
                  </Box>
                  <Typography 
                    variant="h6" 
                    component="h3" 
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Legal Documentation Support
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, textAlign: 'justify' }}>
                    Assistance with drafting and reviewing legal documents for members and clients.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Mentorship Programs */}
            <Grid item xs={12} md={4}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: '#580100',
                    bgcolor: 'rgba(88, 1, 0, 0.02)'
                  }
                }}
              >
                <CardContent sx={{ p: 3, flex: 1 }}>
                  <Box 
                    sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: '12px', 
                      bgcolor: 'rgba(88, 1, 0, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <GroupsIcon sx={{ color: '#580100', fontSize: 24 }} />
                  </Box>
                  <Typography 
                    variant="h6" 
                    component="h3" 
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Mentorship Programs
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, textAlign: 'justify' }}>
                    Connect with experienced advocates for guidance and professional development.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Continuing Legal Education */}
            <Grid item xs={12} md={4}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: '#580100',
                    bgcolor: 'rgba(88, 1, 0, 0.02)'
                  }
                }}
              >
                <CardContent sx={{ p: 3, flex: 1 }}>
                  <Box 
                    sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: '12px', 
                      bgcolor: 'rgba(88, 1, 0, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <SchoolIcon sx={{ color: '#580100', fontSize: 24 }} />
                  </Box>
                  <Typography 
                    variant="h6" 
                    component="h3" 
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Continuing Legal Education
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, textAlign: 'justify' }}>
                    Workshops and seminars to stay updated on legal trends and enhance your expertise.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

export default Home;
